import type { ComponentType } from 'react';
import React, { useState, useCallback, useMemo } from 'react';

import { EditorErrorFlags } from './EditorFlags.tsx';

type EditorFlagsContextType = {
	showEditorFlag: (flagData: ShowFlagProps) => void;
};

const EditorFlagsContext = React.createContext<EditorFlagsContextType>({
	showEditorFlag: () => {},
});

type ShowFlagProps = {
	statusCode?: number;
	lastPublisher?: string;
	collabService?: string;
	errorName: string;
	isUnpublishedDraft?: boolean;
};

export const EditorFlagsContextProvider = ({
	contentId,
	children,
}: {
	contentId: any;
	children: any;
}): React.JSX.Element => {
	const [flag, setFlag] = useState<ShowFlagProps | undefined>(undefined);

	const showEditorFlag = useCallback(
		(flagData: ShowFlagProps) => {
			setFlag(flagData);
		},
		[setFlag],
	);

	// memoize to return the same reference to object and prevent render of multiple error flag with the same message
	const errorData = useMemo(
		() =>
			flag?.errorName
				? {
						[flag.errorName]: true,
					}
				: undefined,
		[flag],
	);

	const contextValue = useMemo(() => {
		return {
			showEditorFlag,
		};
	}, [showEditorFlag]);

	return (
		<EditorFlagsContext.Provider value={contextValue}>
			{children}
			<EditorErrorFlags
				contentId={contentId}
				statusCode={flag?.statusCode}
				errorData={errorData}
				lastPublisher={flag?.lastPublisher}
				collabService={flag?.collabService}
				isUnpublishedDraft={flag?.isUnpublishedDraft}
			/>
		</EditorFlagsContext.Provider>
	);
};

export function withEditorFlagsContext<T>(WrappedComponent: ComponentType<T>) {
	return (props: any): React.JSX.Element => (
		<EditorFlagsContextProvider contentId={props.contentId}>
			<WrappedComponent {...props} />
		</EditorFlagsContextProvider>
	);
}

export const useEditorFlagsContext = (): EditorFlagsContextType => {
	return React.useContext(EditorFlagsContext);
};
